import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { useState } from 'react'
import { printLog } from './printLog'
import { isPlainObject } from 'lodash-es'
import { AxiosCustomError } from './AxiosCustomError'
import { refreshToken } from '../../api/refreshToken'
import { AuthStore } from '../../store/authStore'
import { toast } from 'react-toastify'
import { UserStore } from '../../store/userStore'
import { redirectToAuth } from '../../utils/authRedirect'

export interface IServerError {
  code?: string
  message: string
  customData?: Record<string, any> | null
}

export interface IServerResponseData {
  status: 'error' | 'success'
  data: Record<string, any> | null
  errors: IServerError[]
}

export interface IServerResponse extends AxiosResponse {
  data: IServerResponseData
}

const mockIsNeeded = process.env.REACT_APP_USE_MOCKS === 'true'
let mockIsInitialized = false

const axiosInstance = axios.create({
  withCredentials: true,
  baseURL: process.env.REACT_APP_API_BASE_URL
})
const { interceptors } = axiosInstance

interceptors.request.use(async (config) => {
  config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`

  return config
})

interceptors.response.use(
  async (response: IServerResponse) => {
    void printLog(response)

    const responseData = response.data
    const hasServerErrors: boolean = Boolean(responseData?.errors?.length)

    if (!isPlainObject(responseData)) {
      throw new AxiosCustomError(response)
    }

    if (hasServerErrors) {
      const { message, code } = responseData.errors[0]

      throw new AxiosCustomError(response, message, code)
    }

    return response
  },
  async (error) => {
    const originalRequest = { ...error.config }
    originalRequest._isRetry = true
    const isRetry: boolean = originalRequest._isRetry

    if (error.response?.status === 401 && Boolean(error.config) && !isRetry) {
      try {
        const res = await axiosInstance(refreshToken())
        localStorage.setItem('token', res.data.data)
        UserStore.updateUserData()

        return await axiosInstance.request(originalRequest)
      } catch (e) {
        AuthStore.isUserAuthenticated = false
        if (localStorage.getItem('token')) {
          toast.error('Сессия истекла. Пожалуйста, авторизуйтесь')
          localStorage.removeItem('token')
          UserStore.clearUserData()
        }
        redirectToAuth()
        console.error(e)
      }
    }

    throw error
  }
)

export interface IUseAxiosResponse {
  loading: boolean
  error: any
  response: IServerResponseData | null
  fire: (
    config: AxiosRequestConfig
  ) => Promise<{ response: IServerResponseData | null; error: any }>
}

const useAxios = (): IUseAxiosResponse => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<any>(null)
  const [response, setResponse] = useState<IServerResponseData | null>(null)

  const fire = async (
    config: AxiosRequestConfig
  ): Promise<{ response: IServerResponseData | null; error: any }> => {
    setLoading(true)
    setError(null)
    setResponse(null)

    let res: IServerResponse | null | undefined
    let _err: any

    try {
      res = await axiosInstance(config)

      setResponse(res?.data ?? null)
    } catch (e: any) {
      console.error(e)
      setError(e)

      _err = e
    } finally {
      setLoading(false)

      // eslint-disable-next-line no-unsafe-finally
      return {
        response: res?.data ?? null,
        error: _err
      }
    }
  }

  return { loading, error, response, fire }
}

async function initializeMock(): Promise<void> {
  if (mockIsInitialized || !mockIsNeeded) {
    return
  }

  const { default: MockAdapter } = await import('axios-mock-adapter')
  const { mockServer } = await import('../../server')
  const mock = new MockAdapter(axiosInstance)

  mockServer(mock)

  mockIsInitialized = true
}

export { axiosInstance, useAxios, initializeMock }
